import { useMemo } from 'react';
// @ts-expect-error untyped JS service
import {
  useCustomLookupTables,
  useSystemLookupTables,
  useCreateCustomLookupTable,
  useUpdateCustomLookupTable,
  useDeleteCustomLookupTable,
  useUploadCustomLookupTable
} from '../../../../../services/lookup.service';
import { apiToDict, dictToApiPayload } from '../adapters';
import type { LookupDictionary } from '../types';

export interface UseLookupDictionariesArgs {
  periodId?: string;
  reportType?: string;
}

export interface UseLookupDictionariesResult {
  general: LookupDictionary[];
  custom: LookupDictionary[];
  all: LookupDictionary[];
  isLoading: boolean;
  saveDictionary: (dict: LookupDictionary) => Promise<unknown>;
  createDictionary: (dict: LookupDictionary) => Promise<unknown>;
  deleteDictionary: (id: string) => Promise<unknown>;
  uploadCsv: (args: { file: File; name?: string }) => Promise<unknown>;
  isMutating: boolean;
  isDeleting: boolean;
}

const normalize = (data: unknown): any[] => {
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.data)) return obj.data as any[];
    if (Array.isArray(obj.tables)) return obj.tables as any[];
    if (Array.isArray(obj.results)) return obj.results as any[];
  }
  return [];
};

export const useLookupDictionaries = ({
  periodId,
  reportType
}: UseLookupDictionariesArgs): UseLookupDictionariesResult => {
  const systemQ = useSystemLookupTables(reportType) as { data?: any; isLoading: boolean };
  const customQ = useCustomLookupTables(periodId) as { data?: any; isLoading: boolean };

  const create = useCreateCustomLookupTable(periodId);
  const update = useUpdateCustomLookupTable(periodId);
  const remove = useDeleteCustomLookupTable(periodId);
  const upload = useUploadCustomLookupTable(periodId);

  const general = useMemo<LookupDictionary[]>(
    () => normalize(systemQ.data).map(t => apiToDict(t, 'system')),
    [systemQ.data]
  );
  const custom = useMemo<LookupDictionary[]>(
    () => normalize(customQ.data).map(t => apiToDict(t, 'custom')),
    [customQ.data]
  );
  const all = useMemo(() => [...general, ...custom], [general, custom]);

  const saveDictionary = (dict: LookupDictionary) => {
    if (dict.readonly) return Promise.resolve();
    const payload = dictToApiPayload(dict);
    // Updates use the existing id; creates from the AddDictionaryDrawer go
    // through createDictionary instead (their id is a temporary client id).
    return update.mutateAsync({ tableId: dict.id, data: payload });
  };

  const createDictionary = (dict: LookupDictionary) => {
    const payload = dictToApiPayload(dict);
    return create.mutateAsync(payload);
  };

  const deleteDictionary = (id: string) => remove.mutateAsync({ tableId: id });

  const uploadCsv = ({ file, name }: { file: File; name?: string }) =>
    upload.mutateAsync({ file, name });

  return {
    general,
    custom,
    all,
    isLoading: systemQ.isLoading || customQ.isLoading,
    saveDictionary,
    createDictionary,
    deleteDictionary,
    uploadCsv,
    isMutating: create.isLoading || update.isLoading || remove.isLoading || upload.isLoading,
    isDeleting: remove.isLoading
  };
};
